import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, CircularProgress } from "@mui/material";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { z } from "zod";

import { EventRecord } from "@/shared/domain/events";

import { EventNameInput } from "./EventNameInput";
interface EventNameFormProps {
  name: EventRecord["name"];
  onSubmit: (eventName: string) => Promise<void>;
  onCancel: () => void;
  inputClassName?: CSSModuleClasses[string];
}

const schema = z.object({
  name: z.string().min(1, "Name is required."),
});

export function EventNameForm({
  name,
  onSubmit,
  onCancel,
  inputClassName,
}: EventNameFormProps) {
  const form = useForm<{ name: string }>({
    defaultValues: {
      name,
    },
    resolver: zodResolver(schema),
  });

  const submissionErrors = form.formState.errors.root?.serverErrors;

  const onSubmitData: SubmitHandler<{ name: string }> = (data) => {
    if (data.name === name) {
      // Return early if the name didn't change
      onCancel();
      return;
    }

    return onSubmit(data.name);
  };

  return (
    <form
      onSubmit={(event) => {
        form
          .handleSubmit(onSubmitData)(event)
          .catch(() => {
            form.setError("root.serverErrors", {
              message: "Failed to save event name. Please try again.",
            });
          });
      }}
    >
      <Controller
        control={form.control}
        name="name"
        render={({ field }) => {
          return (
            <EventNameInput
              currentName={field.value}
              onNameChanged={field.onChange}
              size="small"
              autoFocus
              disabled={form.formState.isSubmitting}
              error={form.formState.errors.name !== undefined}
              helperText={form.formState.errors.name?.message}
              inputClassName={inputClassName}
            />
          );
        }}
      />
      {submissionErrors && (
        <Box component="p" sx={{ color: "red", margin: "0.5rem 0" }}>
          {submissionErrors.message}
        </Box>
      )}
      <div
        style={{
          display: "flex",
          gap: "0.5rem",
          margin: "0.5rem 0",
          justifyContent: "flex-end",
        }}
      >
        <Button
          type="button"
          style={{ textTransform: "none" }}
          size="small"
          onClick={() => onCancel()}
        >
          Cancel
        </Button>
        <Button
          style={{ textTransform: "none" }}
          size="small"
          disabled={form.formState.isSubmitting}
          endIcon={
            form.formState.isSubmitting && <CircularProgress size="1.2rem" />
          }
          variant="contained"
          type="submit"
          disableElevation
        >
          Save
        </Button>
      </div>
    </form>
  );
}
